import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import AppleLogin from 'react-apple-login';
import { appleSignInState } from '../../_utils/appleSignIn';
import AuthLayout from './AuthLayout';
import LoginCarousel from './LoginCarousel';
import BeuLoader from '../common/BeuLoader';
import { userActions } from '../../_actions';
import siteSetting from '../../config/env';
import './auth.css';

class Login extends Component {
  constructor(props) {
    super(props);
    
    // Get return URL from query params
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('returnUrl');
    
    this.state = {
      email: '',
      password: '',
      emailError: '',
      passwordError: '',
      error: '',
      isLoading: false,
      showPassword: false,
      returnUrl: returnUrl ? decodeURIComponent(returnUrl) : null,
      initialLoading: true
    };
    
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.validateEmail = this.validateEmail.bind(this);
    this.handleSuccessfulLogin = this.handleSuccessfulLogin.bind(this);
  }

  componentDidMount() {
    // Clear any body scroll lock left behind by a mid-lock navigation, and
    // start at the top (see ClientSignup.componentDidMount).
    const bs = document.body.style;
    if (bs.position === 'fixed') {
      bs.overflow = '';
      bs.position = '';
      bs.width = '';
      bs.top = '';
    }
    window.scrollTo(0, 0);

    // Show beu loader for 1.5 seconds on initial load
    setTimeout(() => {
      this.setState({ initialLoading: false });
    }, 1500);
  }

  handleSuccessfulLogin(response) {
    const { returnUrl } = this.state;
    const hasPendingBooking = localStorage.getItem('pendingBooking') !== null;
    
    // Check if this is a booking flow (returnUrl contains booking params or salon page)
    const isBookingFlow = returnUrl && (
      returnUrl.includes('booking-slot') ||
      returnUrl.includes('/salon/') ||
      hasPendingBooking
    );
    
    if (isBookingFlow) {
      // Client booking flow - redirect back to booking page with params
      window.location.href = returnUrl;
    } else if (response.data.data.userInfo &&
               response.data.data.userInfo.hairdresser &&
               response.data.data.userInfo.hairdresser.salon) {
      // Professional login - redirect to diary
      const salonId = response.data.data.userInfo.hairdresser.salon._id;
      localStorage.setItem('salonId', salonId);
      localStorage.setItem('defaultRedirectTo', response.data.data.userInfo.defaultRedirectTo);
      localStorage.setItem('isShow', response.data.data.userInfo.hairdresser.salon.isShow);
      window.location.href = `/diary/${salonId}`;
    } else if (returnUrl) {
      // Has a return URL - redirect there
      window.location.href = returnUrl;
    } else {
      // Regular client login - redirect to bookings
      this.props.history.push('/dashboard/bookings');
    }
  }

  handleChange(e) {
    const { name, value } = e.target;
    this.setState({ 
      [name]: value,
      [`${name}Error`]: '',
      error: ''
    });
  }

  validateEmail(email) {
    const pattern = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return pattern.test(email);
  }

  handleSubmit(e) {
    e.preventDefault();
    
    // Reset errors
    this.setState({
      emailError: '',
      passwordError: '',
      error: ''
    });

    const { email, password } = this.state;
    let hasError = false;

    // Validation
    if (!email) {
      this.setState({ emailError: 'Please enter your email address' });
      hasError = true;
    } else if (!this.validateEmail(email)) {
      this.setState({ emailError: 'Please enter a valid email address' });
      hasError = true;
    }

    if (!password) {
      this.setState({ passwordError: 'Please enter your password' });
      hasError = true;
    }

    if (hasError) return;

    // Submit login
    this.setState({ isLoading: true });
    
    const params = {
      email: email,
      password: password,
      deviceToken: 'web',
      deviceTypeId: '2'
    };

    this.props.dispatch(userActions.login(params))
      .then((response) => {
        this.setState({ isLoading: false });
        
        if (response.data.errorCode === 0) {
          // Success
          localStorage.removeItem('persist:root');
          this.handleSuccessfulLogin(response);
        } else {
          // Error
          this.setState({
            error: response.data.error?.responseMessage || 'Login failed. Please try again.'
          });
        }
      })
      .catch((error) => {
        this.setState({
          isLoading: false,
          error: 'An error occurred. Please try again.'
        });
      });
  }

  render() {
    const { email, password, emailError, passwordError, error, isLoading, showPassword, returnUrl, initialLoading } = this.state;
    
    // Show beu loader during initial loading
    if (initialLoading) {
      return <BeuLoader message="Welcome back!" subtitle="Setting things up for you..." icon="user" />;
    }
    
    // Build signup URL - go to /signup/client in booking flow (including salon page), otherwise /signup
    const hasPendingBooking = localStorage.getItem('pendingBooking') !== null;
    const isBookingFlow = returnUrl && (
      returnUrl.includes('booking-slot') ||
      returnUrl.includes('/salon/') ||
      returnUrl.includes('/book/') ||
      hasPendingBooking
    );
    // Always carry the returnUrl — the role selector and client signup both
    // forward it, and dropping it here silently ejects the user from the
    // booking flow after signup.
    const signupUrl = isBookingFlow
      ? `/signup/client?returnUrl=${encodeURIComponent(returnUrl)}`
      : (returnUrl ? `/signup?returnUrl=${encodeURIComponent(returnUrl)}` : '/signup');

    return (
      <AuthLayout rightPanel={<LoginCarousel />}>
        <div className="auth-form-container">
          <div className="auth-top-row">
            <Link to={returnUrl || "/"} className="auth-nav-back">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <div className="auth-logo">
              <img
                src={require('../../assets/images/loginLogo.png')}
                alt="Beu Logo"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
            <div className="auth-top-row-spacer" />
          </div>

          <h1 className="auth-title">Welcome Back</h1>
          <p className="auth-subtitle">Sign in to continue to Beu</p>

          {error && (
            <div className="auth-error auth-mb-md">
              <span>{error}</span>
            </div>
          )}
          {/* Same nudge for every failed sign-in (unknown email or wrong
              password alike): nothing here hints at what exists. */}
          {error && (
            <p className="auth-text-muted auth-mb-md" style={{ textAlign: 'center' }}>
              New to Beu? <Link to={signupUrl} className="auth-link">Create an account</Link>
            </p>
          )}

          <form className="auth-form" onSubmit={this.handleSubmit}>
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                className={`auth-input ${emailError ? 'error' : ''}`}
                placeholder="Enter your email"
                value={email}
                onChange={this.handleChange}
                autoComplete="email"
                autoFocus
              />
              {emailError && (
                <div className="auth-error">
                  <span>{emailError}</span>
                </div>
              )}
            </div>

            <div className="auth-form-group">
              <label className="auth-label" htmlFor="password">Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                className={`auth-input ${passwordError ? 'error' : ''}`}
                placeholder="Enter your password"
                value={password}
                onChange={this.handleChange}
                autoComplete="current-password"
              />
              {passwordError && (
                <div className="auth-error">
                  <span>{passwordError}</span>
                </div>
              )}
              <div className="auth-text-right" style={{ marginTop: '10px' }}>
                <Link to="/forgot-password" className="auth-link auth-link-muted">
                  Forgot Password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              className={`auth-button auth-button-primary ${isLoading ? 'auth-button-loading' : ''}`}
              disabled={isLoading}
            >
              Sign In
            </button>
          </form>

          <div className="auth-divider">
            <span>or</span>
          </div>

          <AppleLogin
            clientId={siteSetting.apple_clientId}
            redirectURI={siteSetting.apple_redirectURI}
            state={appleSignInState()}
            scope="name email"
            responseType="code id_token"
            responseMode="form_post"
            render={(props) => (
              <button
                className="auth-button auth-button-social"
                onClick={props.onClick}
                disabled={props.disabled}
              >
                <svg width="20" height="20" viewBox="0 0 384 512" fill="currentColor">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                </svg>
                Continue with Apple
              </button>
            )}
          />

          <div className="auth-footer-link">
            <span>New to Beu? </span>
            <Link to={signupUrl} className="auth-link">
              Create Account
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }
}

function mapStateToProps(state) {
  const { loggingIn } = state.authentication || {};
  return { loggingIn };
}

export default connect(mapStateToProps)(Login);